'use client';

import {
  CaseStudyBody,
  CaseStudyResultsTable,
} from '@/components/case-study/case-study-body';
import { CaseStudyHero } from '@/components/case-study/case-study-hero';
import { CustomerVideos } from '@/components/sections/customer-videos';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

/* The 87% automation rate appears in five places by house style — H1, dek,
 * stat tile, Results table, and the Impact opener — plus both descriptions in
 * layout.tsx. Change it in all seven, in en.ts and ar.ts, or the page
 * contradicts itself. See the opencx-customer-stories skill. */

const FareHarborCaseStudy = () => {
  const { t, isRTL } = useTranslation();
  return (
    <>
      <CaseStudyHero
        customerName="FareHarbor"
        title={t.customerStories['fareharbor_hero_title']}
        subtitle={t.customerStories['fareharbor_what_we_did']}
        // Tile order is fixed house style: scale → automation rate → quality.
        stats={[
          {
            value: isRTL ? '+٢٣ ألف' : '23,000+',
            label: t.customerStories['Operators served'],
          },
          {
            value: isRTL ? '٨٧٪' : '87%',
            label: t.customerStories['Automation rate'],
          },
          {
            value: isRTL ? t.customerStories['All-time high'] : 'ATH',
            label: t.customerStories['CSAT score'],
          },
        ]}
        videoSrc="/home/stories/fareharbor.mp4"
        videoStartAt={2.14}
      />

      <CaseStudyBody
        aside={{
          logoSrc: '/logos/logo-fareharbor.svg',
          logoAlt: 'FareHarbor',
          company: t.customerStories['fareharbor_company_description'],
          industry: t.customerStories['Travel & Tourism / Booking Software'],
          websiteUrl: 'https://fareharbor.com',
          extras: [
            {
              label: t.customerStories['Parent Company'],
              value: 'Booking Holdings',
            },
            {
              label: t.customerStories.Location,
              value: t.customerStories['Denver, CO, USA'],
            },
          ],
          keyResults: [
            t.customerStories['• 87% automation rate'],
            t.customerStories['• 23,000+ operators served'],
            t.customerStories['• All-time high CSAT score'],
            t.customerStories['• Email, chat, and voice covered'],
          ],
        }}
      >
        <p>{t.customerStories['fareharbor_intro']}</p>

        <h2>{t.customerStories['fareharbor_scale_title']}</h2>
        <p>{t.customerStories['fareharbor_scale_p1']}</p>
        <p>{t.customerStories['fareharbor_scale_p2']}</p>
        <p>{t.customerStories['fareharbor_scale_p3']}</p>
        <p>{t.customerStories['fareharbor_scale_p4']}</p>

        <h2>{t.customerStories['fareharbor_solution_title']}</h2>
        <p>{t.customerStories['fareharbor_solution_p1']}</p>
        <p>{t.customerStories['fareharbor_solution_p2']}</p>
        <p>{t.customerStories['fareharbor_solution_p3']}</p>
        <p>{t.customerStories['fareharbor_solution_p4']}</p>

        <h3>{t.customerStories['The Results']}</h3>
        <p>{t.customerStories['fareharbor_results_intro']}</p>

        <CaseStudyResultsTable
          metricLabel={t.customerStories['Metric']}
          impactLabel={t.customerStories['Impact']}
          rows={[
            {
              metric: t.customerStories['Automation rate'],
              impact: isRTL ? '٨٧٪' : '87%',
            },
            {
              metric: t.customerStories['CSAT score'],
              impact: t.customerStories['All-time high'],
            },
            {
              metric: t.customerStories['Response time'],
              impact: t.customerStories['Under 30 seconds'],
            },
            {
              metric: t.customerStories['Channels covered'],
              impact: t.customerStories['Email, chat, and voice'],
            },
          ]}
        />

        <p>{t.customerStories['fareharbor_results_p1']}</p>

        <h2>{t.customerStories['fareharbor_impact_title']}</h2>
        <p>{t.customerStories['fareharbor_impact_p1']}</p>
        <p>{t.customerStories['fareharbor_impact_p2']}</p>
      </CaseStudyBody>

      <CustomerVideos />

      <Testimonials />
    </>
  );
};

export default FareHarborCaseStudy;
